import { useEffect, useMemo, useRef, useState } from "react";
import { http } from "@/lib/api";
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import { MapContainer, TileLayer, CircleMarker, Popup } from "react-leaflet";
import { Users, Broadcast, HardDrives, Calendar, Cpu, ArrowsDownUp, TrendUp, Timer, SealCheck, Warning, Database, Plugs } from "@phosphor-icons/react";
import { Link, useNavigate } from "react-router-dom";

// Wraps a username label in a link to the subscriber's General page.
// Silently falls back to a plain span when we don't have an id (unknown user,
// legacy RADIUS log with no matching subscriber). Same DOM shape either way
// so the widget row layout stays identical.
const UserLink = ({ username, subId, className = "font-mono truncate" }) => {
  if (!username) return <span className={className}>—</span>;
  if (!subId) return <span className={className}>{username}</span>;
  return (
    <Link
      to={`/users/${subId}`}
      className={`${className} text-primary hover:underline`}
      data-testid={`dashboard-user-link-${subId}`}
      title="Open subscriber's General page"
    >
      {username}
    </Link>
  );
};

const bytesFmt = (n) => {
  if (!n) return "0";
  const u = ["B", "KB", "MB", "GB", "TB"]; let i = 0; let x = n;
  while (x > 1024 && i < u.length - 1) { x /= 1024; i++; }
  return `${x.toFixed(1)} ${u[i]}`;
};
const bpsFmt = (n) => bytesFmt(n) + "/s";
const uptimeFmt = (v) => (typeof v === "string" ? v : "—");
// Border classes for the ok/warn/crit status pill on the Host Disk + FD
// widgets. Matches the existing Kpi tone palette.
const toneCls = (s) =>
  s === "crit" ? "border-destructive/60 text-destructive"
  : s === "warn" ? "border-[hsl(var(--warning))]/60 text-[hsl(var(--warning))]"
  : "border-[hsl(var(--success))]/60 text-[hsl(var(--success))]";

const Kpi = ({ label, value, sub, icon: Icon, tone = "neutral", onClick }) => {
  const toneCls = {
    neutral: "bg-card border-border text-foreground",
    blue: "bg-[hsl(199,89%,48%,0.12)] border-primary/60 text-primary",
    green: "bg-[hsl(148,60%,45%,0.12)] border-[hsl(var(--success))]/60 text-[hsl(var(--success))]",
    purple: "bg-[hsl(258,60%,60%,0.12)] border-[hsl(258,60%,60%)]/60 text-[hsl(258,60%,60%)]",
    yellow: "bg-[hsl(38,92%,50%,0.12)] border-[hsl(var(--warning))]/60 text-[hsl(var(--warning))]",
  }[tone];
  const Cmp = onClick ? "button" : "div";
  return (
    <Cmp onClick={onClick} className={`border p-5 relative overflow-hidden text-left w-full ${toneCls} ${onClick ? "hover:brightness-110 cursor-pointer transition-all hover:-translate-y-0.5" : ""}`} data-testid={`kpi-${label.toLowerCase()}`}>
      <div className="flex items-start justify-between">
        <div className="uppercase-label text-current opacity-80">{label}</div>
        <Icon size={22} className="text-current opacity-80" />
      </div>
      <div className="font-display font-bold text-4xl mt-3 tracking-tight text-current">{value}</div>
      <div className="text-xs mt-1 text-current opacity-70">{sub}</div>
      {onClick && <div className="text-[10px] mt-2 text-current opacity-60 font-mono">click for details →</div>}
    </Cmp>
  );
};

const Widget = ({ title, icon: Icon, children, right }) => (
  <div className="border border-border bg-card">
    <div className="flex items-center justify-between border-b border-border px-4 py-2.5">
      <div className="flex items-center gap-2 text-sm font-semibold"><Icon size={14} className="text-primary" /> {title}</div>
      {right}
    </div>
    <div className="p-4">{children}</div>
  </div>
);

const Row = ({ k, v }) => (
  <div className="flex items-center justify-between py-1.5 border-b border-border/60 last:border-0 text-sm">
    <span className="text-muted-foreground uppercase-label">{k}</span>
    <span className="font-mono text-xs">{v ?? "—"}</span>
  </div>
);

export default function Dashboard() {
  const navigate = useNavigate();
  const [k, setK] = useState(null);
  const [stats, setStats] = useState({});
  const [topUsage, setTopUsage] = useState([]);
  const [topSessions, setTopSessions] = useState([]);
  const [liveByType, setLiveByType] = useState({ Regular: 0, MAC: 0, Lease: 0, Card: 0 });
  const [userStatus, setUserStatus] = useState({ total: 0, active: 0, expired: 0, disabled: 0 });
  const [authLog, setAuthLog] = useState([]);
  const [subs, setSubs] = useState([]);
  const [hostHealth, setHostHealth] = useState(null);

  // Username → subscriber_id lookup used by Top Usage, Top Reconnects, and
  // UserAuthLog widgets so every username in the Dashboard becomes a link to
  // the subscriber's General page. Built once from the already-loaded /subscribers
  // response so we don't need an extra round-trip.
  const subIdByUsername = useMemo(() => {
    const m = {};
    for (const s of subs) if (s.username) m[s.username] = s.id;
    return m;
  }, [subs]);

  // Router picker + live health + interface traffic
  const [routers, setRouters] = useState([]);
  const [routerId, setRouterId] = useState("");
  const [health, setHealth] = useState(null);
  const [ifaces, setIfaces] = useState([]);
  const [iface, setIface] = useState("");
  const [samples, setSamples] = useState([]);
  const trafficTimer = useRef(null);

  useEffect(() => {
    (async () => {
      const [a, b, c, d, e, f, g, s, r] = await Promise.all([
        http.get("/dashboard/kpis"),
        http.get("/dashboard/statistics"),
        http.get("/dashboard/top-usage"),
        http.get("/dashboard/top-sessions"),
        http.get("/dashboard/live-by-type"),
        http.get("/dashboard/user-status"),
        http.get("/dashboard/auth-log?limit=8"),
        http.get("/subscribers"),
        http.get("/dashboard/routers"),
      ]);
      setK(a.data); setStats(b.data); setTopUsage(c.data); setTopSessions(d.data);
      setLiveByType(e.data); setUserStatus(f.data); setAuthLog(g.data); setSubs(s.data);
      setRouters(r.data);
      if (r.data.length) setRouterId(r.data[0].id);
      // Host health is master-admin-only; silently ignore 403 for NAS staff
      // so their Dashboard still hydrates without a scary red console error.
      http.get("/dashboard/host-health").then((res) => setHostHealth(res.data)).catch(() => {});
    })();
  }, []);

  // When router changes, fetch health + interface list, and start live-traffic polling for the first running iface
  useEffect(() => {
    if (!routerId) return;
    let killed = false;
    (async () => {
      try {
        const [h, i, meta] = await Promise.all([
          http.get(`/nas/${routerId}/router-health`),
          http.get(`/nas/${routerId}/interfaces`),
          http.get(`/nas`),
        ]);
        if (killed) return;
        setHealth(h.data);
        // Pin the operator-configured `default_graph_iface` if present — hard lock
        // the picker to only that one interface. If blank, fall back to auto-detect
        // (first running non-virtual interface) and show the full list.
        const nasRow = (meta.data || []).find((n) => n.id === routerId);
        const pinned = (nasRow?.default_graph_iface || "").trim();
        const rows = i.data || [];
        const matched = pinned ? rows.find((x) => x.name === pinned) : null;
        if (matched) {
          setIfaces([matched]);
          setIface(matched.name);
        } else {
          setIfaces(rows);
          const running = rows.find((x) => x.running && !x.name.startsWith("__")) || rows[0];
          if (running && !running.name.startsWith("__")) setIface(running.name);
        }
      } catch {
        setHealth({ reachable: false, error: "fetch failed" });
      }
    })();
    return () => { killed = true; };
  }, [routerId]);

  // Poll interface traffic every 3s
  useEffect(() => {
    if (!routerId || !iface) return;
    setSamples([]);
    const poll = async () => {
      try {
        const { data } = await http.get(`/nas/${routerId}/interface-traffic?iface=${encodeURIComponent(iface)}`);
        setSamples((prev) => {
          const now = new Date();
          const next = [...prev, { t: now.toLocaleTimeString(), tx: data.tx_bps || 0, rx: data.rx_bps || 0 }];
          return next.slice(-30);
        });
      } catch {}
    };
    poll();
    trafficTimer.current = setInterval(poll, 3000);
    return () => clearInterval(trafficTimer.current);
  }, [routerId, iface]);

  const mapPoints = useMemo(() => subs.filter((s) => s.latitude && s.longitude).slice(0, 200), [subs]);

  return (
    <div className="space-y-6 fade-up" data-testid="dashboard-view">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="uppercase-label">NOC overview</div>
          <h1 className="font-display font-bold text-4xl mt-1">Dashboard</h1>
        </div>
        <div className="font-mono text-xs text-muted-foreground">{new Date().toLocaleString()}</div>
      </div>

      {/* KPI row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Kpi label="Users"    value={k?.total_users ?? userStatus.total ?? "—"}   sub="Total users till now" icon={Users}       tone="blue"   onClick={() => navigate("/subscribers?filter=all")} />
        <Kpi label="Online"   value={Object.values(liveByType).reduce((a,b)=>a+b,0)} sub="Currently connected"   icon={Broadcast}   tone="green"  onClick={() => navigate("/subscribers?filter=online")} />
        <Kpi label="Active"   value={userStatus.active}                                sub="Total active users"   icon={SealCheck} tone="purple" onClick={() => navigate("/subscribers?filter=all")} />
        <Kpi label="Renewals" value={stats.expiring_tomorrow ?? stats.expiring_15_days ?? 0}                       sub="Expiring tomorrow"  icon={Calendar}    tone="yellow" onClick={() => navigate("/reports?tab=renewals&window=tomorrow")} />
      </div>

      {/* Interface Traffic — live per-router */}
      <Widget
        title="Interface Traffic"
        icon={ArrowsDownUp}
        right={
          <div className="flex items-center gap-2">
            <select value={routerId} onChange={(e) => setRouterId(e.target.value)} data-testid="router-picker" className="bg-secondary border border-border text-xs h-7 px-2 font-mono">
              {routers.length === 0 && <option value="">No router — add MikroTik API creds on any NAS</option>}
              {routers.map((r) => <option key={r.id} value={r.id}>{r.name} · {r.nas_ip}</option>)}
            </select>
            {ifaces.length > 0 && !ifaces[0].name.startsWith("__") && (
              <select value={iface} onChange={(e) => setIface(e.target.value)} data-testid="iface-picker" className="bg-secondary border border-border text-xs h-7 px-2 font-mono">
                {ifaces.map((i) => <option key={i.name} value={i.name}>{i.name}{i.running ? " ●" : ""}</option>)}
              </select>
            )}
          </div>
        }
      >
        {routers.length === 0 && (
          <div className="text-xs text-muted-foreground py-6 text-center">
            Add <code>api_user</code> / <code>api_password</code> on any NAS in <b>NAS Devices</b> and it will show up here.
          </div>
        )}
        {routers.length > 0 && (
          <div className="h-56">
            <ResponsiveContainer>
              <LineChart data={samples} margin={{ top: 6, right: 10, left: -10, bottom: 0 }}>
                <CartesianGrid stroke="hsl(var(--border))" vertical={false} />
                <XAxis dataKey="t" stroke="hsl(var(--muted-foreground))" fontSize={10} />
                <YAxis tickFormatter={bytesFmt} stroke="hsl(var(--muted-foreground))" fontSize={10} />
                <Tooltip formatter={(v) => bpsFmt(v)} contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", fontSize: 12 }} />
                <Line type="monotone" dataKey="tx" name="Tx" stroke="hsl(148 60% 45%)" dot={false} strokeWidth={1.5} isAnimationActive={false} />
                <Line type="monotone" dataKey="rx" name="Rx" stroke="hsl(var(--primary))" dot={false} strokeWidth={1.5} isAnimationActive={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}
      </Widget>

      {/* Router Health · Statistics · Top Usage · Top Sessions row */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
        <Widget title="Router Health" icon={Cpu}>
          {!health && <div className="text-xs text-muted-foreground">Select a router.</div>}
          {health && !health.reachable && <div className="text-xs text-destructive">Unreachable: {health.error}</div>}
          {health?.reachable && (
            <div>
              <Row k="Uptime" v={uptimeFmt(health.uptime)} />
              <Row k="CPU-Load" v={`${health.cpu_load}%`} />
              <Row k="Total-Memory" v={bytesFmt(health.total_memory)} />
              <Row k="Free-Memory" v={bytesFmt(health.free_memory)} />
              <Row k="Total-Disk" v={bytesFmt(health.total_hdd)} />
              <Row k="Free-Disk" v={bytesFmt(health.free_hdd)} />
              <Row k="Board-Name" v={health.board_name} />
            </div>
          )}
        </Widget>

        <Widget title="Statistics" icon={TrendUp}>
          <Row k="Tomorrow expire"    v={stats.expiring_tomorrow ?? stats.expiring_15_days ?? 0} />
          <Row k="Today new conn."    v={stats.today_connections} />
          <Row k="This month new conn." v={stats.this_month_connections} />
          <Row k="Last month new conn." v={stats.last_month_connections} />
        </Widget>

        <Widget title="Top Usage (30d)" icon={ArrowsDownUp}>
          {topUsage.length === 0 && <div className="text-xs text-muted-foreground">No usage yet.</div>}
          {topUsage.map((r) => (
            <div key={r.subscriber_id} className="flex items-center justify-between text-xs py-1.5 border-b border-border/60 last:border-0">
              <UserLink username={r.username} subId={r.subscriber_id || subIdByUsername[r.username]} />
              <span className="font-mono text-primary">{bytesFmt(r.bytes)}</span>
            </div>
          ))}
        </Widget>

        <Widget title="Top Reconnects (today)" icon={Timer}>
          {topSessions.length === 0 && <div className="text-xs text-muted-foreground">No disconnects since 00:00 UTC.</div>}
          {topSessions.map((s) => (
            <div key={s.username} className="flex items-center justify-between text-xs py-1.5 border-b border-border/60 last:border-0">
              <UserLink username={s.username} subId={subIdByUsername[s.username]} />
              <span className="font-mono text-primary" title={s.last_disconnect_ts ? `Last: ${new Date(s.last_disconnect_ts).toLocaleTimeString()}` : ""}>{s.disconnects_today}×</span>
            </div>
          ))}
        </Widget>
      </div>

      {/* Ops Health · surfaces the two things that already brought the
          server down in FU 78 (disk full → mongo crash) and FU 80 (backend
          FD ulimit → dashboard all-zeros). Master admin only; NAS staff
          get a 403 which the loader silently swallows so their Dashboard
          still renders cleanly. */}
      {hostHealth && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Widget title="Host Disk" icon={Database} right={<span className={`text-[10px] uppercase tracking-wide px-2 py-0.5 border ${toneCls(hostHealth.disk.status)}`}>{hostHealth.disk.status}</span>}>
            <Row k="Used" v={<span className={`font-mono ${hostHealth.disk.status === "crit" ? "text-destructive font-semibold" : hostHealth.disk.status === "warn" ? "text-[hsl(var(--warning))] font-semibold" : ""}`}>{hostHealth.disk.used_pct}% · {hostHealth.disk.used_gb}G / {hostHealth.disk.total_gb}G</span>} />
            <Row k="Free" v={<span className="font-mono">{hostHealth.disk.free_gb} G</span>} />
            <Row k="Warn / Crit" v={<span className="font-mono text-muted-foreground">{hostHealth.disk.threshold_warn_pct}% / {hostHealth.disk.threshold_crit_pct}%</span>} />
            {hostHealth.disk.status !== "ok" && (
              <div className="mt-3 text-xs flex items-start gap-2 text-destructive"><Warning size={14} className="shrink-0 mt-0.5" /><span>Mongo will refuse to write once disk hits 100%. Run <code className="font-mono">docker builder prune -af</code> and truncate large <code className="font-mono">/var/lib/docker/containers/*/*.log</code> files now.</span></div>
            )}
          </Widget>
          <Widget title="Backend File Descriptors" icon={Plugs} right={<span className={`text-[10px] uppercase tracking-wide px-2 py-0.5 border ${toneCls(hostHealth.fds.status)}`}>{hostHealth.fds.status}</span>}>
            <Row k="Open" v={<span className={`font-mono ${hostHealth.fds.status === "crit" ? "text-destructive font-semibold" : hostHealth.fds.status === "warn" ? "text-[hsl(var(--warning))] font-semibold" : ""}`}>{hostHealth.fds.used} / {hostHealth.fds.limit}</span>} />
            <Row k="Utilisation" v={<span className="font-mono">{hostHealth.fds.used_pct}%</span>} />
            <Row k="Warn / Crit" v={<span className="font-mono text-muted-foreground">{hostHealth.fds.threshold_warn} / {hostHealth.fds.threshold_crit}</span>} />
            {hostHealth.fds.status !== "ok" && (
              <div className="mt-3 text-xs flex items-start gap-2 text-destructive"><Warning size={14} className="shrink-0 mt-0.5" /><span>Backend is leaking sockets. Restart <code className="font-mono">sns-backend</code> to release FDs, then check <code className="font-mono">mikrotik_client._close</code> is actually closing the underlying transport.</span></div>
            )}
          </Widget>
        </div>
      )}

      {/* LiveByType · UserStatus · Map row */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Widget title="LiveByType" icon={Broadcast}>
          {["Regular", "MAC", "Lease", "Card"].map((t) => <Row key={t} k={`${t} User`} v={liveByType[t] ?? 0} />)}
        </Widget>
        <Widget title="UserStatus" icon={SealCheck}>
          <Row k="Total"   v={userStatus.total} />
          <Row k="Active"  v={userStatus.active} />
          <Row k="Expired" v={userStatus.expired} />
          <Row k="Disabled" v={userStatus.disabled} />
        </Widget>
        <Widget title="Client Locations" icon={HardDrives}>
          <div className="h-40">
            <MapContainer center={[22.9, 78.9]} zoom={4} style={{ height: "100%", width: "100%", background: "#0f1720" }}>
              <TileLayer attribution="&copy; OSM" url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
              {mapPoints.map((p) => (
                <CircleMarker key={p.id} center={[p.latitude, p.longitude]} radius={4} pathOptions={{ color: p.connection_status === "online" ? "hsl(148 60% 45%)" : "#666", fillOpacity: 0.8 }}>
                  <Popup>{p.full_name} · {p.connection_status}</Popup>
                </CircleMarker>
              ))}
            </MapContainer>
          </div>
        </Widget>
      </div>

      {/* UserAuthLog */}
      <Widget title="UserAuthLog" icon={Broadcast}>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-border">
                <th className="text-left py-2 uppercase-label">Username</th>
                <th className="text-left py-2 uppercase-label">Event</th>
                <th className="text-left py-2 uppercase-label">NAS</th>
                <th className="text-left py-2 uppercase-label">Framed-IP</th>
                <th className="text-right py-2 uppercase-label">When</th>
              </tr>
            </thead>
            <tbody>
              {authLog.map((r, i) => (
                <tr key={i} className="border-b border-border/50" data-testid="authlog-row">
                  <td className="py-1.5"><UserLink username={r.username} subId={subIdByUsername[r.username]} className="font-mono" /></td>
                  <td className="py-1.5"><span className={`font-mono text-[10px] px-1.5 py-0.5 border ${r.event === "ACCT-STOP" ? "border-destructive/50 text-destructive" : "border-primary/50 text-primary"}`}>{r.event}</span></td>
                  <td className="py-1.5 font-mono text-muted-foreground">{r.nas_name || r.nas_ip || "—"}</td>
                  <td className="py-1.5 font-mono text-muted-foreground">{r.framed_ip || "—"}</td>
                  <td className="py-1.5 text-right font-mono text-muted-foreground">{new Date(r.ts).toLocaleTimeString()}</td>
                </tr>
              ))}
              {authLog.length === 0 && <tr><td colSpan={5} className="text-center py-6 text-muted-foreground">No recent auth events.</td></tr>}
            </tbody>
          </table>
        </div>
      </Widget>
    </div>
  );
}
